import { WarningRounded } from "@mui/icons-material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LoadingButton from "../Shared/LoadingButton";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { FinchatMessagePayload } from "../../assets/interfaces/interfaces";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { finchatMessageLoad } from "../../redux/slices/finchatSlice";
import ChatMessage from "../ChatMessage";
import { makeUserMessage } from "../TradeGPT";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";

type PreviewWarningProps = {
  handleClose: () => void;
  queryToPreview: string;
};
const PreviewWarning = ({ handleClose, queryToPreview }: PreviewWarningProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showPreviewError, setShowPreviewError] = useState(false);
  const plan = useSelector((state: { plan }) => state.plan?.plan);
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: { auth }) => state.auth.currentUser);

  const sessionIdRef = useRef(uuidv4());
  const session_id = sessionIdRef.current;

  const finchatMessage = useSelector(
    (state: RootState) => state?.finchat?.finchatMessages[session_id],
  );

  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!queryToPreview) return;

    setMessages(() => {
      const userMessage = makeUserMessage(queryToPreview);

      let botMessage = null;
      if (finchatMessage?.output?.length && finchatMessage?.chat_id) {
        botMessage = {
          outputLoading: finchatMessage.outputLoading,
          output: finchatMessage.output,
          isBot: true,
          chart_data: finchatMessage.chart_data,
          chart_flag: finchatMessage.chart_flag,
          chat_id: finchatMessage.chat_id,
          toolLoaders: finchatMessage.toolLoaders,
        };
      }

      return botMessage ? [userMessage, botMessage] : [userMessage];
    });
  }, [queryToPreview, finchatMessage]);

  const handlePreviewTask = (text: string) => {
    if (plan.promptsLeft === 0) {
      setShowPreviewError(true);
      return;
    }
    // don't dispatch message load if there is already existing output
    if (finchatMessage?.output && finchatMessage?.chat_id) {
      setShowPreview(true);
      return;
    };
    try {
      const chat_id = uuidv4();
      const payload: FinchatMessagePayload = {
        input: text,
        chat_id,
        user_id: user?.userId,
        input_type: "scheduled_task",
        session_id,
      };
      dispatch(
        finchatMessageLoad(payload),
      );
      setShowPreview(true);
    }
    catch (e) {
      console.error(e);
    }
  };

  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [finchatMessage?.output]);

  return (
    <div className="h-[422px] dark:bg-background/paper-elevation-8 border border-elevation-outlined">
      {!showPreview
      && (
        <div className="h-full flex flex-col gap-y-6 justify-center items-center">
          <WarningRounded className={`${showPreviewError ? "!fill-error-light" : "!fill-text-disabled"} !w-[36px] !h-[36px]`} />
          <div className="flex flex-col gap-y-2 items-center">
            <Typography variant="h6/medium-500" className="dark:text-white">{showPreviewError ? "Preview unavailable" : "Preview will use a prompt"}</Typography>
            <Typography variant="body2/regular-400" className="dark:text-mui-white-70 px-16 text-center">
              {showPreviewError
                ? "You’ve reached your daily prompt limit. Upgrade to TradeGPT Pro or try again tomorrow."
                : `You have ${plan.promptsLeft} free prompts left today.`}
            </Typography>
          </div>
          <div className="flex gap-x-4">
            {showPreviewError
              ? (
                  <Button
                    variant="outlined"
                    className="!border-primary-black-states-outlinedBorder hover:bg-tgpt-secondary-states-hover !normal-case !text-primary-black-main"
                    onClick={handleClose}
                    fullWidth
                  >
                    Back
                  </Button>
                )
              : (
                  <>
                    <Button
                      variant="outlined"
                      className="!border-primary-black-states-outlinedBorder hover:bg-tgpt-secondary-states-hover !normal-case !text-primary-black-main"
                      onClick={handleClose}
                      fullWidth
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      isLoading={showPreview}
                      onClick={() => handlePreviewTask(queryToPreview || "")}
                      variant="contained"
                      fullWidth
                      className="text-nowrap !min-w-[156px]"
                      size="medium"
                    >
                      Continue Preview
                    </LoadingButton>
                  </>
                )}
          </div>
        </div>
      )}
      {showPreview && (
        <div className="flex flex-col overflow-y-auto items-start h-full custom-scrollbar box-content me-[4px]" ref={containerRef}>
          <IconButton
            className="!h-8 !w-8 z-20"
            onClick={() => {
              setShowPreview(false);
            }}
            sx={{ position: "absolute", top: 32, right: 32, zIndex: 20 }}
          >
            <CloseRounded className="text-mui-black-38 dark:text-mui-white-38" />
          </IconButton>
          {messages.length > 0 && messages.map(message => (
            <ChatMessage message={message} key={message.chat_id} />
          ))}
          {(!finchatMessage?.output.length) && (
            <ChatMessage message={finchatMessage?.output ?? ""} loading={true} sessionId={session_id} />
          )}
        </div>
      )}
    </div>
  );
};

export default PreviewWarning;
